import React, { useState } from 'react'
import { AlertCircle, ChevronDown, ChevronRight, GripVertical, Plus, Trash2 } from 'lucide-react'
import Button from '../../ui/Button'
import Input from '../../ui/Input'
import Select from '../../ui/Select'
import CodeItemReferencePicker from './CodeItemReferencePicker'
import type { AppendixCodeItemOption, AppendixColumnDataType } from '../../../types/codeIndex'
import {
  APPENDIX_COLUMN_TYPE_OPTIONS,
  AppendixColumnDraft,
  AppendixColumnError,
  AppendixRefIndex,
  columnHasUnit,
  formatColumnPillMeta,
  formatCount,
  codeItemChoice,
  findCodeItemOption,
  formatColumnHeader,
  getColumnRefState,
} from '../../../utils/appendixTable'

type AppendixColumnsSectionProps = {
  columns: AppendixColumnDraft[]
  // Picker options built from the saved reference targets (empty until loaded, or in a frozen revision).
  codeItems: AppendixCodeItemOption[]
  refIndex: AppendixRefIndex
  columnErrors: Record<string, AppendixColumnError>
  firstColumnError: string | null
  expanded: boolean
  autoFocusColumnKey: string | null
  disabled?: boolean
  readOnly?: boolean
  definesValidCombinations?: boolean
  onToggleExpanded: () => void
  onAddColumn: () => void
  onUpdateColumn: (columnKey: string, patch: Partial<Pick<AppendixColumnDraft, 'label' | 'unit'>>) => void
  // `choice` is the picked option (codeItemChoice) of a code item column.
  onRequestTypeChange: (columnKey: string, dataType: AppendixColumnDataType, choice?: string) => void
  onDeleteColumn: (columnKey: string) => void
  onMoveColumn: (fromIndex: number, toIndex: number) => void
}

const FIELD_LABEL_CLASS = 'mb-1 block text-xs font-medium text-gray-600'

const CHIP_TONES = {
  navy: 'bg-[#1D5176] text-white',
  light: 'bg-[#DCEBFA] text-[#1D5176]',
  broken: 'bg-[#FDECEA] text-[#B3261E]',
}

export const CodeItemChip: React.FC<{ code: string; tone?: keyof typeof CHIP_TONES; title?: string }> = ({ code, tone = 'navy', title }) => (
  <span
    title={title}
    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none ${CHIP_TONES[tone]}`}
  >
    Code item {code}
  </span>
)

// The chip of a code item column: the code item's current code, or that it no longer exists.
export const ColumnCodeItemChip: React.FC<{
  column: AppendixColumnDraft
  refIndex: AppendixRefIndex
  tone?: 'navy' | 'light'
}> = ({ column, refIndex, tone = 'navy' }) => {
  const state = getColumnRefState(column, refIndex)
  if (state.status === 'Missing') return <CodeItemChip code="No longer exists" tone="broken" />
  if (!state.codeItem) return null

  const details = [
    `Code item ${state.codeItem.code}`,
    state.codeItem.number ? `No. ${state.codeItem.number}` : '',
    state.codeItem.title,
  ].filter(Boolean).join(' - ')
  return (
    <CodeItemChip
      code={state.codeItem.code}
      tone={state.status === 'PositionOutOfRange' ? 'broken' : tone}
      title={details}
    />
  )
}

const ColumnPill: React.FC<{
  column: AppendixColumnDraft
  index: number
  codeItems: AppendixCodeItemOption[]
  refIndex: AppendixRefIndex
}> = ({ column, index, codeItems, refIndex }) => {
  const label = column.label.trim() || `Column ${index + 1}`

  if (column.dataType === 'CodeItem') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF6FF] px-2.5 py-1 text-xs">
        <ColumnCodeItemChip column={column} refIndex={refIndex} />
        <span className="font-medium text-[#1D74D8]" title={formatColumnHeader(column, label, codeItems)}>{formatColumnHeader(column, label, codeItems)}</span>
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F5F9] px-2.5 py-1 text-xs">
      <span className="font-medium text-gray-800">{label}</span>
      <span className="text-gray-500">{formatColumnPillMeta(column)}</span>
    </span>
  )
}

type CodeItemColumnFieldsProps = {
  column: AppendixColumnDraft
  position: number
  codeItems: AppendixCodeItemOption[]
  selected: AppendixCodeItemOption | null
  refIndex: AppendixRefIndex
  invalid: boolean
  disabled: boolean
  readOnly: boolean
  removeDisabled: boolean
  choosing: boolean
  onChoose: () => void
  onRemove: () => void
  onSelect: (choice: string) => void
}

// The code item a column points at. A code item that no longer exists offers to choose another or
// remove the column; a position range that no longer exists asks for another position. Neither
// ever shows the code the column was saved with.
const CodeItemColumnFields: React.FC<CodeItemColumnFieldsProps> = ({
  column,
  position,
  codeItems,
  selected,
  refIndex,
  invalid,
  disabled,
  readOnly,
  removeDisabled,
  choosing,
  onChoose,
  onRemove,
  onSelect,
}) => {
  const state = getColumnRefState(column, refIndex)
  const isMissing = state.status === 'Missing'

  if (isMissing && !choosing) {
    return (
      <div data-testid="broken-code-item-column">
        <span className={FIELD_LABEL_CLASS}>Which code item</span>
        <p className="flex items-center gap-1.5 text-xs font-medium text-[#B3261E]">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          Code item no longer exists
        </p>
        {!readOnly ? (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <button type="button" className="cursor-pointer text-xs font-medium text-[#1D74D8] hover:underline disabled:cursor-not-allowed disabled:opacity-50" onClick={onChoose} disabled={disabled}>
              Choose code item
            </button>
            <button
              type="button"
              className="cursor-pointer text-xs font-medium text-[#B3261E] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onRemove}
              disabled={disabled || removeDisabled}
              title={removeDisabled ? 'Turn off valid combinations before removing the last code item column.' : undefined}
            >
              Remove column
            </button>
          </div>
        ) : null}
      </div>
    )
  }

  const outOfRange = state.status === 'PositionOutOfRange'
  return (
    <>
      <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-code-item`}>Which code item</label>
      <CodeItemReferencePicker
        items={codeItems}
        selected={selected}
        invalid={invalid || isMissing || outOfRange}
        ariaLabel={`Column ${position} code item`}
        onSelect={onSelect}
        disabled={disabled}
      />
      {outOfRange ? (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-[#B3261E]">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {`${state.codeItem.code} no longer has this position. Choose a position.`}
        </p>
      ) : null}
    </>
  )
}

export const AppendixColumnsSection: React.FC<AppendixColumnsSectionProps> = ({
  columns,
  codeItems,
  refIndex,
  columnErrors,
  firstColumnError,
  expanded,
  autoFocusColumnKey,
  disabled = false,
  readOnly = false,
  definesValidCombinations = false,
  onToggleExpanded,
  onAddColumn,
  onUpdateColumn,
  onRequestTypeChange,
  onDeleteColumn,
  onMoveColumn,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  // Broken columns whose author chose to pick another code item.
  const [choosingKeys, setChoosingKeys] = useState<Record<string, boolean>>({})
  const inputsDisabled = disabled || readOnly

  const endDrag = () => {
    setDraggedIndex(null)
    setDropIndex(null)
  }

  return (
    <section className="rounded-lg border border-[#E2E8F0]">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="flex min-w-0 cursor-pointer items-center gap-2 text-left"
        >
          {expanded
            ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-[#1D5176]" aria-hidden="true" />
            : <ChevronRight className="h-4 w-4 flex-shrink-0 text-[#1D5176]" aria-hidden="true" />}
          <span className="text-sm font-semibold text-gray-900">Columns</span>
          <span className="text-sm text-gray-500">
            {columns.length > 0 ? formatCount(columns.length, 'column') : 'No columns yet'}
          </span>
        </button>
        {!readOnly ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="bg-[#EEF6FF]"
            onClick={onAddColumn}
            disabled={disabled}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Add Column
          </Button>
        ) : null}
      </div>

      {expanded ? (
        <div className="space-y-2 px-4 pb-4">
          {columns.length === 0 ? (
            <p className="text-sm text-gray-500">Define the structure before entering rows.</p>
          ) : (
            columns.map((column, index) => {
              const position = index + 1
              const error = columnErrors[column.key]
              const lastReference = definesValidCombinations && column.dataType === 'CodeItem'
                && columns.filter(item => item.dataType === 'CodeItem').length === 1
              const selected = findCodeItemOption(codeItems, column)
              const isDropTarget = dropIndex === index && draggedIndex !== null && draggedIndex !== index

              return (
                <div
                  key={column.key}
                  data-testid="appendix-column-card"
                  onDragOver={(event) => {
                    if (draggedIndex === null) return
                    event.preventDefault()
                    setDropIndex(index)
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    if (draggedIndex !== null) onMoveColumn(draggedIndex, index)
                    endDrag()
                  }}
                  className={`grid grid-cols-[20px_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_32px] items-end gap-3 rounded-md border px-3 py-2 transition-colors focus-within:border-[#1D74D8] focus-within:bg-[#F1F7FD] ${isDropTarget ? 'border-[#1D74D8] bg-[#F1F7FD]' : 'border-[#E2E8F0] bg-white'} ${draggedIndex === index ? 'opacity-60' : ''}`}
                >
                  {readOnly ? <span /> : (
                    <span
                      draggable={!disabled}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move'
                        event.dataTransfer.setData('text/plain', column.key)
                        const card = event.currentTarget.parentElement
                        if (card && typeof event.dataTransfer.setDragImage === 'function') {
                          event.dataTransfer.setDragImage(card, 16, 16)
                        }
                        setDraggedIndex(index)
                      }}
                      onDragEnd={endDrag}
                      aria-label={`Drag column ${position}`}
                      className="flex h-8 cursor-grab items-center justify-center text-gray-300 hover:text-gray-500"
                    >
                      <GripVertical className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}

                  <div className="min-w-0">
                    <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-label`}>Label</label>
                    <Input
                      id={`${column.key}-label`}
                      value={column.label}
                      invalid={error?.field === 'label'}
                      autoFocus={column.key === autoFocusColumnKey}
                      aria-label={`Column ${position} label`}
                      onChange={(event) => onUpdateColumn(column.key, { label: event.target.value })}
                      disabled={inputsDisabled}
                    />
                  </div>

                  <div className="min-w-0">
                    <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-type`}>Type</label>
                    <Select
                      id={`${column.key}-type`}
                      value={column.dataType}
                      aria-label={`Column ${position} type`}
                      title={lastReference ? 'Turn off valid combinations before changing the last code item column to another type.' : undefined}
                      onChange={(event) => onRequestTypeChange(
                        column.key,
                        event.target.value as AppendixColumnDataType,
                        selected ? codeItemChoice(selected) : ''
                      )}
                      disabled={inputsDisabled}
                    >
                      {(column.dataType === 'String' || column.dataType === 'Boolean') ? <option value={column.dataType}>{column.dataType} (existing)</option> : null}
                      {APPENDIX_COLUMN_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value} disabled={(option.value === 'CodeItem' && codeItems.length === 0) || (lastReference && option.value !== 'CodeItem')}>{option.label}</option>
                      ))}
                    </Select>
                    {codeItems.length === 0 ? <p className="mt-1 text-xs text-gray-500">No code items on this codesheet.</p> : null}
                  </div>

                  <div className="min-w-0">
                    {column.dataType === 'CodeItem' ? (
                      <CodeItemColumnFields
                        column={column}
                        position={position}
                        codeItems={codeItems}
                        selected={selected}
                        refIndex={refIndex}
                        invalid={error?.field === 'codeItem'}
                        disabled={inputsDisabled}
                        readOnly={readOnly}
                        removeDisabled={lastReference}
                        choosing={Boolean(choosingKeys[column.key])}
                        onChoose={() => setChoosingKeys(current => ({ ...current, [column.key]: true }))}
                        onRemove={() => onDeleteColumn(column.key)}
                        onSelect={choice => onRequestTypeChange(column.key, 'CodeItem', choice)}
                      />
                    ) : columnHasUnit(column.dataType) ? (
                      <>
                        <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-unit`}>Unit</label>
                        <Input
                          id={`${column.key}-unit`}
                          value={column.unit}
                          placeholder="Eg. MBh"
                          aria-label={`Column ${position} unit`}
                          onChange={(event) => onUpdateColumn(column.key, { unit: event.target.value })}
                          disabled={inputsDisabled}
                        />
                      </>
                    ) : <span className="block h-8" />}
                  </div>

                  {readOnly ? <span /> : (
                    <button
                      type="button"
                      onClick={() => onDeleteColumn(column.key)}
                      disabled={disabled || lastReference}
                      title={lastReference ? 'Turn off valid combinations before removing the last code item column.' : undefined}
                      aria-label={`Remove column ${position}`}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-[#1D5176] hover:bg-[#EEF6FF] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              )
            })
          )}
          {firstColumnError ? (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-[#B3261E]">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              {firstColumnError}
            </p>
          ) : null}
        </div>
      ) : columns.length > 0 ? (
        <div className="flex flex-wrap gap-2 px-4 pb-4" aria-label="Column summary">
          {columns.map((column, index) => (
            <ColumnPill key={column.key} column={column} index={index} codeItems={codeItems} refIndex={refIndex} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

export default AppendixColumnsSection
